/**
 * Tenant-aware date formatting utilities.
 *
 * Reads the tenant's date_format and timezone preferences from localStorage
 * and applies consistent formatting across the entire frontend.
 *
 * Uses date-fns v3 + date-fns-tz v3 for timezone-aware formatting.
 */

import { format, parseISO, isValid, differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { isValidTimeZone } from './tenantTime';

// Mapping from tenant date_format string → date-fns format tokens
const FORMAT_MAP = {
  'DD MMM YYYY': {
    date: 'dd MMM yyyy',               // 13 Feb 2026
    datetime: 'dd MMM yyyy, hh:mm a',  // 13 Feb 2026, 02:30 PM
    reportDate: 'dd MMMM yyyy',        // 13 February 2026
    reportTimestamp: "dd MMMM yyyy 'at' hh:mm a", // 13 February 2026 at 02:30 PM
    time: 'hh:mm a',                   // 02:30 PM
    month: 'MMM yyyy',                 // Feb 2026
  },
  'MM/DD/YYYY': {
    date: 'MM/dd/yyyy',               // 02/13/2026
    datetime: 'MM/dd/yyyy hh:mm a',   // 02/13/2026 02:30 PM
    reportDate: 'MMMM dd, yyyy',      // February 13, 2026
    reportTimestamp: "MMMM dd, yyyy 'at' hh:mm a", // February 13, 2026 at 02:30 PM
    time: 'hh:mm a',                  // 02:30 PM
    month: 'MMM yyyy',                // Feb 2026
  },
  'DD/MM/YYYY': {
    date: 'dd/MM/yyyy',               // 13/02/2026
    datetime: 'dd/MM/yyyy hh:mm a',   // 13/02/2026 02:30 PM
    reportDate: 'dd MMMM yyyy',       // 13 February 2026
    reportTimestamp: "dd MMMM yyyy 'at' hh:mm a", // 13 February 2026 at 02:30 PM
    time: 'hh:mm a',                  // 02:30 PM
    month: 'MMM yyyy',                // Feb 2026
  },
  'YYYY-MM-DD': {
    date: 'yyyy-MM-dd',               // 2026-02-13
    datetime: 'yyyy-MM-dd HH:mm',     // 2026-02-13 14:30
    reportDate: 'yyyy-MM-dd',         // 2026-02-13
    reportTimestamp: 'yyyy-MM-dd HH:mm', // 2026-02-13 14:30
    time: 'HH:mm',                    // 14:30
    month: 'yyyy-MM',                 // 2026-02
  },
};

const DEFAULT_FORMAT = 'DD MMM YYYY';
const EMPTY = '\u2014'; // em-dash for null/invalid

/**
 * Read the tenant's preferred date_format from localStorage.
 * Falls back to DEFAULT_FORMAT if not set.
 */
export function getTenantDateFormat() {
  try {
    const raw = localStorage.getItem('tenant');
    if (raw) {
      const tenant = JSON.parse(raw);
      if (tenant.date_format && FORMAT_MAP[tenant.date_format]) {
        return tenant.date_format;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_FORMAT;
}

/**
 * Read the tenant's timezone from localStorage.
 * Returns an IANA timezone string like "Asia/Kolkata".
 * Falls back to 'UTC' if not set.
 */
export function getTenantTimezone() {
  try {
    const raw = localStorage.getItem('tenant');
    if (raw) {
      const tenant = JSON.parse(raw);
      if (tenant.timezone) {
        return tenant.timezone;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return 'UTC';
}

/**
 * Safely parse a value into a valid Date object.
 * Accepts Date, ISO string, or null. Returns null on failure.
 */
function toDate(value) {
  if (value == null) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === 'string') {
    try {
      const d = parseISO(value);
      return isValid(d) ? d : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Check if a value is a date-only string (no time component).
 * Date-only strings like "2026-02-13" should not have timezone conversion
 * applied since they represent calendar dates, not moments in time.
 */
function isDateOnly(value) {
  return typeof value === 'string' && !value.includes('T');
}

function getTokens(key) {
  const fmt = getTenantDateFormat();
  return FORMAT_MAP[fmt]?.[key] || FORMAT_MAP[DEFAULT_FORMAT][key];
}

/**
 * Format a Date in the tenant's timezone using the given pattern.
 * Falls back to plain format if timezone is invalid.
 */
function formatInTenantTz(date, pattern) {
  const tz = getTenantTimezone();
  try {
    return formatInTimeZone(date, tz, pattern);
  } catch {
    return format(date, pattern);
  }
}

/**
 * Get the timezone abbreviation for the tenant's timezone.
 * e.g., "IST", "SGT", "UTC"
 */
function getTzAbbreviation() {
  const tz = getTenantTimezone();
  if (tz === 'UTC') return 'UTC';
  try {
    return formatInTimeZone(new Date(), tz, 'zzz');
  } catch {
    return 'UTC';
  }
}

/** Date only — e.g. "13 Feb 2026" */
export function formatDate(value) {
  const d = toDate(value);
  if (!d) return EMPTY;
  // Date-only strings: no timezone conversion (they represent calendar dates)
  if (isDateOnly(value)) {
    return format(d, getTokens('date'));
  }
  return formatInTenantTz(d, getTokens('date'));
}

/** Date + time — e.g. "13 Feb 2026, 02:30 PM" */
export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return EMPTY;
  return formatInTenantTz(d, getTokens('datetime'));
}

/** Report header — e.g. "13 February 2026" */
export function formatReportDate(value) {
  const d = toDate(value);
  if (!d) return EMPTY;
  if (isDateOnly(value)) {
    return format(d, getTokens('reportDate'));
  }
  return formatInTenantTz(d, getTokens('reportDate'));
}

/** Report timestamp — e.g. "13 February 2026 at 02:30 PM IST" */
export function formatReportTimestamp(value) {
  const d = toDate(value);
  if (!d) return EMPTY;
  const base = formatInTenantTz(d, getTokens('reportTimestamp'));
  return base + ' ' + getTzAbbreviation();
}

/** Time only — e.g. "02:30 PM" */
export function formatTime(value) {
  const d = toDate(value);
  if (!d) return EMPTY;
  return formatInTenantTz(d, getTokens('time'));
}

/**
 * Today's calendar date as "YYYY-MM-DD", from the browser's LOCAL wall clock.
 *
 * Use this for <input type="date"> value/min/max instead of
 * `new Date().toISOString().split('T')[0]`, which yields the UTC date. For a
 * user ahead of UTC (e.g. IST between 00:00 and 05:30 local) the UTC date is
 * still "yesterday", so a `max` computed that way rejects the user's own today.
 */
export function todayLocalISO() {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Today's calendar date as "YYYY-MM-DD" in the TENANT's timezone
 * (getTenantTimezone()), not the browser's or UTC.
 *
 * Use for <input type="date"> min/max bounds that must agree with the
 * tenant's site calendar (e.g. a Malaysia tenant viewed from a UTC browser).
 * Invalid/unknown zones fall back to UTC, matching the backend default.
 */
export function todayTenantISO() {
  const tz = getTenantTimezone();
  const zone = isValidTimeZone(tz) ? tz : 'UTC';
  // Assemble from parts rather than trusting en-CA's separator/order.
  const p = {};
  new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date()).forEach(({ type, value }) => { p[type] = value; });
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Current local wall-clock time as "HH:MM" (24h) — the shape expected by
 * <input type="time"> and by the incident_time / observation_time columns
 * (VARCHAR(10)).
 */
export function nowLocalHHMM() {
  return format(new Date(), 'HH:mm');
}

/**
 * Relative time — "5m ago" / "2h ago" / "3d ago", then falls back to formatDate.
 */
export function formatTimeAgo(value) {
  const d = toDate(value);
  if (!d) return EMPTY;

  const now = new Date();
  const mins = differenceInMinutes(now, d);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;

  const hrs = differenceInHours(now, d);
  if (hrs < 24) return `${hrs}h ago`;

  const days = differenceInDays(now, d);
  if (days < 7) return `${days}d ago`;

  return formatDate(value);
}

/** Month + year — e.g. "Feb 2026" */
export function formatMonth(value) {
  const d = toDate(value);
  if (!d) return EMPTY;
  if (isDateOnly(value)) {
    return format(d, getTokens('month'));
  }
  return formatInTenantTz(d, getTokens('month'));
}
